import { useState, useRef } from 'react';
import { Plus, X, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { getVisibleDefs, getExtraCategories, isArrayDef, getEffectiveFilterDefs, getAllDefs, optionLabel } from '@/utils/filterDefinitions';
import { useFilterLayout } from '@/store/useFilterLayout';
import { FilterField } from './FilterField';

function AddFilterDrawer({ extraFields, toggleExtra }) {
  const [open, setOpen] = useState(false);
  const categories = getExtraCategories();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="flex-1 rounded-lg border-dashed border-slate-300 text-slate-600 hover:bg-slate-50" data-testid="add-filter-button">
          <Plus size={16} className="mr-1.5" />Add Field
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[360px] overflow-y-auto">
        <SheetHeader><SheetTitle>Add filters</SheetTitle></SheetHeader>
        <div className="mt-6 space-y-6">
          {Object.entries(categories).map(([cat, defs]) => (
            <div key={cat}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{cat}</p>
              <div className="mt-2 grid grid-cols-1 gap-2">
                {defs.map((d) => (
                  <label key={d.id} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <Checkbox checked={extraFields.includes(d.id)} onCheckedChange={() => toggleExtra(d.id)} data-testid={`add-filter-${d.id}`} />
                    {d.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ActiveChips({ filters, onRemove }) {
  const chips = [];
  getEffectiveFilterDefs().forEach((def) => {
    if (def.keys) {
      const [lo, hi] = def.keys;
      const a = filters[lo];
      const b = filters[hi];
      if (a || b) {
        chips.push({ key: def.keys, label: def.label });
      }
    } else if (isArrayDef(def)) {
      const vals = Array.isArray(filters[def.key]) ? filters[def.key] : [];
      vals.forEach((val) => {
        chips.push({ key: def.key, value: val, label: optionLabel(def, val) });
      });
    } else if (filters[def.key]) {
      chips.push({ key: def.key, label: def.label });
    }
  });

  if (!chips.length) return null;

  return (
    <div className="border-b border-slate-200 px-4 py-3">
      <div className="flex flex-wrap gap-1.5">
        {chips.slice(0, 1).map((c, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onRemove(c.key, c.value)}
            className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
          >
            Active
            <X size={13} />
          </button>
        ))}
      </div>
    </div>
  );
}

export const FilterSidebar = ({ filters, onChange }) => {
  const { extraFields, fieldOrder, toggleExtra, removeExtra, setOrder } = useFilterLayout();
  const defs = getVisibleDefs(extraFields, fieldOrder);

  const [dragOverId, setDragOverId] = useState(null);
  const [dragEnabledId, setDragEnabledId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const dragIdRef = useRef(null);

  const clearValue = (def) => {
    if (def.keys) onChange(Object.fromEntries(def.keys.map((k) => [k, ''])));
    else onChange({ [def.key]: isArrayDef(def) ? [] : '' });
  };

  const handleRemove = (def) => { clearValue(def); removeExtra(def.id); };

  const clearAllValues = () => {
    const patch = {};
    getAllDefs().forEach((def) => {
      if (def.keys) def.keys.forEach((k) => { patch[k] = ''; });
      else patch[def.key] = isArrayDef(def) ? [] : '';
    });
    onChange(patch);
  };

  const removeChip = (key, value) => {
    if (Array.isArray(key)) return onChange(Object.fromEntries(key.map((k) => [k, ''])));
    if (value != null) return onChange({ [key]: (filters[key] || []).filter((v) => v !== value) });
    onChange({ [key]: '' });
  };

  const handleDragStart = (e, id) => {
    dragIdRef.current = id;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragIdRef.current) setDragOverId(id);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = dragIdRef.current;
    if (!sourceId || sourceId === targetId) return;
    const ids = defs.map((d) => d.id);
    const fromIdx = ids.indexOf(sourceId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const newIds = [...ids];
    newIds.splice(fromIdx, 1);
    newIds.splice(toIdx, 0, sourceId);
    setOrder(newIds);
    setDragOverId(null);
    setDraggingId(null);
    setDragEnabledId(null);
    dragIdRef.current = null;
  };

  const handleDragEnd = () => {
    setDragOverId(null);
    setDraggingId(null);
    setDragEnabledId(null);
    dragIdRef.current = null;
  };

  const defaultOpen = ['status', 'propertyType'];

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex-1 overflow-y-auto">
        <div className="flex items-center gap-2 px-4 py-3">
          <AddFilterDrawer extraFields={extraFields} toggleExtra={toggleExtra} />
        </div>
        <Accordion type="multiple" defaultValue={defaultOpen} className="space-y-0">
          {defs.map((def) => (
            <div
              key={def.id}
              draggable={dragEnabledId === def.id}
              onDragStart={(e) => handleDragStart(e, def.id)}
              onDragOver={(e) => handleDragOver(e, def.id)}
              onDrop={(e) => handleDrop(e, def.id)}
              onDragEnd={handleDragEnd}
              className={cn(
                'relative',
                draggingId === def.id && 'opacity-50',
                dragOverId === def.id && 'border-t-2 border-blue-500',
              )}
            >
              <AccordionItem value={def.id} className="border-b border-slate-100 px-4">
                <AccordionTrigger className="py-3 text-sm font-semibold text-slate-900 hover:no-underline">
                  <div className="flex flex-1 items-center gap-2 pr-2">
                    <span
                      className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0"
                      onMouseDown={() => setDragEnabledId(def.id)}
                      onMouseUp={() => { if (dragIdRef.current === null) setDragEnabledId(null); }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Drag to reorder"
                    >
                      <GripVertical size={14} />
                    </span>
                    <span className="flex-1 text-left">{def.label}</span>
                    {def.removable && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRemove(def); }}
                        className="text-slate-300 hover:text-red-500"
                        aria-label={`Remove ${def.label}`}
                        data-testid={`remove-filter-${def.id}`}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pb-2">
                    <FilterField def={def} filters={filters} onChange={onChange} hideLabel />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </div>
          ))}
        </Accordion>
      </div>
    </div>
  );
};
